/**
 * Translation Loader - Automatic loading of translation JSON files
 * 
 * Automatically loads translation JSON files and registers them with TranslationRegistry.
 * Supports versioning via cache manifest.
 */
(function(window) {
    'use strict';
    
    const TranslationLoader = {
        /**
         * Load translations from JSON files defined in data attributes or script tags
         * Looks for <script type="application/json" data-translation-module="moduleName" src="path/to/translations.json">
         * or calls loadFromJSONFiles() with provided files array
         * 
         * @param {Array<{path: string, module: string}>} files - Optional array of files to load
         * @returns {Promise<void>}
         */
        load: async function(files = null) {
            if (!window.TranslationRegistry) {
                console.error('[TranslationLoader] TranslationRegistry not found');
                return;
            }
            
            // If files array provided, use it
            if (files && Array.isArray(files)) {
                await window.TranslationRegistry.loadFromJSONFiles(files);
                return;
            }
            
            // Otherwise, find all script tags with data-translation-module attribute
            const scriptTags = document.querySelectorAll('script[type="application/json"][data-translation-module]');
            const loadPromises = [];
            
            for (const script of scriptTags) {
                const moduleName = script.getAttribute('data-translation-module');
                const src = script.getAttribute('src');
                
                if (src && moduleName) {
                    loadPromises.push(
                        window.TranslationRegistry.loadFromJSON(src, moduleName)
                    );
                }
            }
            
            await Promise.all(loadPromises);
        },
        
        /**
         * Load translations for a specific page
         * @param {string} page - Page name ('search', 'lectures', 'admin', etc.)
         * @returns {Promise<void>}
         */
        loadForPage: async function(page) {
            const publicPageTranslations = {
                'search': [
                    { path: '/static/js/translations/common/translations.json', module: 'common' },
                    { path: '/static/js/translations/pages/search/translations.json', module: 'search' },
                    { path: '/static/js/translations/modules/auth/translations.json', module: 'auth' },
                    { path: '/static/js/translations/pages/adm/translations.json', module: 'admin' }
                ],
                'lectures': [
                    { path: '/static/js/translations/common/translations.json', module: 'common' },
                    { path: '/static/js/translations/pages/lectures/translations.json', module: 'lectures' },
                    { path: '/static/js/translations/modules/auth/translations.json', module: 'auth' },
                    { path: '/static/js/translations/modules/onboarding/translations.json', module: 'onboarding' }
                ],
                'login': [
                    { path: '/static/js/translations/common/translations.json', module: 'common' },
                    { path: '/static/js/translations/pages/login/translations.json', module: 'login' },
                    { path: '/static/js/translations/modules/auth/translations.json', module: 'auth' }
                ],
            };
            
            // Check if server provided restricted page translations (only for authorized users)
            const restrictedPageTranslations = window.__pageTranslations || {};
            
            // Merge public and restricted translations
            const pageTranslations = Object.assign({}, publicPageTranslations, restrictedPageTranslations);
            
            const files = pageTranslations[page] || [];
            if (files.length > 0) {
                await this.load(files);
            }
        }
    };
    
    // Export to global namespace
    window.TranslationLoader = TranslationLoader;
    
    // Auto-load on DOM ready if data attributes are present
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            TranslationLoader.load();
        });
    } else {
        // DOM already loaded
        TranslationLoader.load();
    }
    
})(window);

